import { t } from "@lingui/macro";
import { CommonActions, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { fetchQuery, useRelayEnvironment } from "react-relay";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { sessionAtom } from "../../../features/Session";
import { BaseNavigatorParamList } from "../../../navigators/BaseNavigator/types";
import { SessionCheckerQueryHandlerQueryString } from "../../../navigators/SessionChecker/SessionCheckerQueryHandler";
import { useMutationWithError } from "../../../utils/hooks/useMutationWithError";
import {
  PasswordResetInput,
  useRegisterNewAccountMutation,
  useRegisterNewAccountMutation$data,
} from "./__generated__/useRegisterNewAccountMutation.graphql";

export const useRegisterNewAccount = () => {
  const [tryRegister, isRegistering] =
    useMutationWithError<useRegisterNewAccountMutation>(RegisterMutationString);
  const navigation =
    useNavigation<NativeStackNavigationProp<BaseNavigatorParamList>>();

  const relayEnvironment = useRelayEnvironment();

  const setSession = useSetAtom(sessionAtom);

  const onCompleted = (res: useRegisterNewAccountMutation$data) => {
    if (!!res.responsePayload?.success) {
      fetchQuery(
        relayEnvironment,
        SessionCheckerQueryHandlerQueryString,
        {}
      ).subscribe({
        error: (error: any) => {
          // if this fails, just send them to the login screen as before
          //  TODO Do we want to log the error?
          showToast({
            description: t`Well look at you go! Your account's all created. You can now log in.`,
            semanticStyle: "success",
            toastId: "registration-success",
          });
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ name: "Unauthenticated" }],
            })
          );
        },
        next: (data: any) => {
          if (data?.session?.user) {
            const user = data.session?.user;
            setSession({
              user: {
                id: user.id,
                email: user.email!,
                username: user.username!,
                clubs:
                  user.memberProfiles?.edges?.map((edge: any) => ({
                    id: edge?.node?.member?.club?.id || "",
                    slug: edge?.node?.member?.club?.slug || "",
                    name: edge?.node?.member?.club?.name || "",
                    logoImage:
                      edge?.node?.member?.club?.logoImage?.publicId || "",
                  })) || [],
              },
            });
            showToast({
              description: t`Well look at you go! Your account's all created. You are now logged in.`,
              semanticStyle: "success",
              toastId: "registration-success",
            });
            const slug =
              user.memberProfiles?.edges?.[0]?.node?.member?.club?.slug || "";
            if (slug) {
              navigation.dispatch(
                CommonActions.reset({
                  index: 0,
                  routes: [{ name: "Club", params: { clubSlug: slug } }],
                })
              );
            }
          }
        },
      });
    }
  };

  const { showToast } = useToast();

  const doRegister = (input: PasswordResetInput) => {
    tryRegister({
      variables: { input },
      onCompleted: (res) => onCompleted(res),
    });
  };

  return [doRegister, isRegistering] as const;
};

export const RegisterMutationString = graphql`
  mutation useRegisterNewAccountMutation($input: PasswordResetInput!) {
    responsePayload: passwordReset(input: $input) {
      success
      errors {
        code

        message
      }
    }
  }
`;
